(ns app.front.form.responsive-table
  "Functions for making a responsive (screen size dependant) form table.
  The functions accept a screen width breakpoint below which the table is broken into sections"
  (:require [app.front.util :as futil]))


(def min-column-counts-for-breakpoints
  "See tailwind.config.js for the pixel widths of the breakpoints.
  These are the heuristic column counts at which points a given table should collapse based on the number of columns
  it has"
  {:md       2
   :lg       4
   :xl       6
   :xxl      9
   :collapse 10})


(defn get-breakpoint-by-col-count
  "Returns the breakpoint for a responsive form table with the given number of columns"
  [col-count]
  (->> min-column-counts-for-breakpoints
       (sort-by second)
       (reduce
         (fn [breakpoint [next-breakpoint next-col-min]]
           (if (<= next-col-min col-count)
             next-breakpoint
             breakpoint))
         :sm)))


(def breakpoint-classes
  "Tailwind will optimize these out if they are not spelled out like this"
  {:col-header {:sm  "bold hidden sm:block"
                :md  "bold hidden md:block"
                :lg  "bold hidden lg:block"
                :xl  "bold hidden xl:block"
                :xxl "bold hidden xxl:block"}
   :empty-cell {:sm  "hidden sm:block"
                :md  "hidden md:block"
                :lg  "hidden lg:block"
                :xl  "hidden xl:block"
                :xxl "hidden xxl:block"}
   :cell-label {:sm  "sm:hidden"
                :md  "md:hidden"
                :lg  "lg:hidden"
                :xl  "xl:hidden"
                :xxl "xxl:hidden"}
   :field      {:sm  "sm:min-h-16"
                :md  "md:min-h-16"
                :lg  "lg:min-h-16"
                :xl  "xl:min-h-16"
                :xxl "xxl:min-h-16"}
   :row-header {:sm  "font-bold pt-2 sm:min-h-16 sm:font-normal"
                :md  "font-bold pt-2 md:min-h-16 md:font-normal"
                :lg  "font-bold pt-2 lg:min-h-16 lg:font-normal"
                :xl  "font-bold pt-2 xl:min-h-16 xl:font-normal"
                :xxl "font-bold pt-2 xxl:min-h-16 xxl:font-normal"}
   :first-cell {:sm  "sm:mt-0 sm:grid-cols-1"
                :md  "md:mt-0 md:grid-cols-1"
                :lg  "lg:mt-0 lg:grid-cols-1"
                :xl  "xl:mt-0 xl:grid-cols-1"
                :xxl "xxl:mt-0 xxl:grid-cols-1"}
   :other-cell {:sm  "sm:grid-cols-1"
                :md  "md:grid-cols-1"
                :lg  "lg:grid-cols-1"
                :xl  "xl:grid-cols-1"
                :xxl "xxl:grid-cols-1"}})


(def table-class-by-col-count
  "Breakpoints for the responsive table for all Tailwind column counts and breakpoints"
  {1  {:sm  "sm:grid-cols-1"
       :md  "md:grid-cols-1"
       :lg  "lg:grid-cols-1"
       :xl  "xl:grid-cols-1"
       :xxl "xxl:grid-cols-1"}
   2  {:sm  "sm:grid-cols-2"
       :md  "md:grid-cols-2"
       :lg  "lg:grid-cols-2"
       :xl  "xl:grid-cols-2"
       :xxl "xxl:grid-cols-2"}
   3  {:sm  "sm:grid-cols-3"
       :md  "md:grid-cols-3"
       :lg  "lg:grid-cols-3"
       :xl  "xl:grid-cols-3"
       :xxl "xxl:grid-cols-3"}
   4  {:sm  "sm:grid-cols-4"
       :md  "md:grid-cols-4"
       :lg  "lg:grid-cols-4"
       :xl  "xl:grid-cols-4"
       :xxl "xxl:grid-cols-4"}
   5  {:sm  "sm:grid-cols-5"
       :md  "md:grid-cols-5"
       :lg  "lg:grid-cols-5"
       :xl  "xl:grid-cols-5"
       :xxl "xxl:grid-cols-5"}
   6  {:sm  "sm:grid-cols-6"
       :md  "md:grid-cols-6"
       :lg  "lg:grid-cols-6"
       :xl  "xl:grid-cols-6"
       :xxl "xxl:grid-cols-6"}
   7  {:sm  "sm:grid-cols-7"
       :md  "md:grid-cols-7"
       :lg  "lg:grid-cols-7"
       :xl  "xl:grid-cols-7"
       :xxl "xxl:grid-cols-7"}
   8  {:sm  "sm:grid-cols-8"
       :md  "md:grid-cols-8"
       :lg  "lg:grid-cols-8"
       :xl  "xl:grid-cols-8"
       :xxl "xxl:grid-cols-8"}
   9  {:sm  "sm:grid-cols-9"
       :md  "md:grid-cols-9"
       :lg  "lg:grid-cols-9"
       :xl  "xl:grid-cols-9"
       :xxl "xxl:grid-cols-9"}
   10 {:sm  "sm:grid-cols-10"
       :md  "md:grid-cols-10"
       :lg  "lg:grid-cols-10"
       :xl  "xl:grid-cols-10"
       :xxl "xxl:grid-cols-10"}
   11 {:sm  "sm:grid-cols-11"
       :md  "md:grid-cols-11"
       :lg  "lg:grid-cols-11"
       :xl  "xl:grid-cols-11"
       :xxl "xxl:grid-cols-11"}
   12 {:sm  "sm:grid-cols-12"
       :md  "md:grid-cols-12"
       :lg  "lg:grid-cols-12"
       :xl  "xl:grid-cols-12"
       :xxl "xxl:grid-cols-12"}})


(defn header
  "Create a header row that is hidden under the breakpoint width when the table is collapsed into a list"
  [{:keys [breakpoint cells]}]
  (futil/for-frag [cell cells]
    [:div
     {:class (-> breakpoint-classes :col-header breakpoint (or "hidden"))}
     cell]))


(defn row
  "Create a row of cells that collapses into a list of fields under the breakpoint width.
  The collapsed list has no headers; instead the header data is used to label the fields.
  A cell that has been designated as the 'row-header' will be used as the title of the 'row section'
  in the collapsed view.
  [:remove-row-btn ...] 'element/cell' can be used to display a button that removes the current row
  Note the use of `futil/for-frag`, required by the Tailwind grid system."
  [{:keys [breakpoint render-fn headers cells]}]
  (futil/for-frag-indexed [[cell-idx [_ {:keys [empty-cell? row-header?]} & _ :as cell]] cells]
    (let [;; Hide the field label in the non-collapsed (table) view
          label-div [:div {:class ["my-2" (-> breakpoint-classes :cell-label breakpoint)]}
                     (get headers cell-idx)]
          ;; Use the header row as the title of the subsection in the collapsed view
          field-div [:div.pr-4 {:class (if row-header?
                                         (-> breakpoint-classes :row-header breakpoint)
                                         (-> breakpoint-classes :field breakpoint))}
                     (render-fn cell)]]
      (if empty-cell?
        [:div {:class (-> breakpoint-classes :empty-cell breakpoint)}]
        [:div {:class ["grid grid-cols-1 items-center"
                       (if (= 0 cell-idx) ; Put some space between sections in the collapsed view
                         (str "mt-16 " (-> breakpoint-classes :first-cell breakpoint))
                         (-> breakpoint-classes :other-cell breakpoint))]}
         (when-not row-header? label-div)
         field-div]))))


(defn table
  "Creates a table that responds to Tailwind breakpoints"
  [{:keys [breakpoint col-count class]} & children]
  (into [:div.grid.gap-2
         {:class [(get-in table-class-by-col-count [col-count breakpoint])
                  class]}]
        children))
